import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAppSelector, useAppDispatch } from "../app/hooks"
import { fetchCompanies } from "../features/companies/companySlice"
import { deleteCompany } from "../features/companies/companyAPI"
import { ProfileImage } from "../components/ProfileImage"
import { toast } from "sonner"

const CompanyList = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const accessToken = useAppSelector((state) => state.auth.accessToken)
    const companies = useAppSelector((state) => state.companies.companies)
    const companiesStatus = useAppSelector((state) => state.companies.status)
    const companiesError = useAppSelector((state) => state.companies.error)

    const [currentPage, setCurrentPage] = useState(1)
    const [deletingId, setDeletingId] = useState<number | null>(null)
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [companyToDelete, setCompanyToDelete] = useState<number | null>(null)

    const ITEMS_PER_PAGE = 7
    const MAX_VISIBLE_PAGES = 9

    // Calculate pagination for client-side pagination
    const totalPages = Math.ceil(companies.length / ITEMS_PER_PAGE)
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
    const endIndex = startIndex + ITEMS_PER_PAGE
    const displayedCompanies = companies.slice(startIndex, endIndex)

    // Calculate sliding window for pagination
    const getPaginationWindow = () => {
        if (totalPages <= MAX_VISIBLE_PAGES) {
            return { startPage: 1, endPage: totalPages }
        }

        let startPage: number

        if (currentPage === 1) {
            startPage = 1
        } else if (currentPage === totalPages) {
            startPage = totalPages - MAX_VISIBLE_PAGES + 1
        } else if (currentPage <= MAX_VISIBLE_PAGES) {
            startPage = 1
        } else if (currentPage > totalPages - MAX_VISIBLE_PAGES) {
            startPage = totalPages - MAX_VISIBLE_PAGES + 1
        } else {
            startPage = currentPage - MAX_VISIBLE_PAGES + 1
        }

        const endPage = Math.min(startPage + MAX_VISIBLE_PAGES - 1, totalPages)
        return { startPage, endPage }
    }

    const { startPage, endPage } = getPaginationWindow()
    const visiblePages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)

    // Fetch companies on mount
    useEffect(() => {
        if (!accessToken) return
        dispatch(fetchCompanies({ accessToken }))
    }, [dispatch, accessToken])

    const handleEdit = (companyId: number, e: React.MouseEvent) => {
        e.stopPropagation()
        navigate(`/company-profile/${companyId}`)
    }

    const openDeleteModal = (companyId: number, e: React.MouseEvent) => {
        e.stopPropagation()
        setCompanyToDelete(companyId)
        setDeleteModalOpen(true)
    }

    const closeDeleteModal = () => {
        setDeleteModalOpen(false)
        setCompanyToDelete(null)
    }

    const handleConfirmDelete = async () => {
        if (companyToDelete === null || !accessToken) {
            if (!accessToken) toast.error("Authentication required")
            closeDeleteModal()
            return
        }

        const companyId = companyToDelete
        closeDeleteModal()
        setDeletingId(companyId)
        try {
            const result = await deleteCompany(companyId, accessToken)
            if (result.success) {
                toast.success("Company deleted successfully")
                dispatch(fetchCompanies({ accessToken }))
            } else {
                toast.error(result.error || "Failed to delete company")
            }
        } catch (error) {
            toast.error("An error occurred while deleting the company")
            console.error("Delete error:", error)
        } finally {
            setDeletingId(null)
        }
    }

    return (
        <div className="min-h-screen p-5">
            <div className="mx-auto max-w-7xl">
                {/* Header Section */}
                <div className="flex flex-col items-end gap-11">
                    <button
                        onClick={() => navigate('/company-profile')}
                        className="btn-cta flex items-center gap-2 rounded-md mb-6 px-6 py-3 text-sm font-semibold">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        Add Company
                    </button>
                </div>

                {/* Table */}
                <div className="overflow-hidden rounded-md">
                    <div className="overflow-x-auto">
                        <table className="w-full table-fixed border-collapse">
                            <colgroup>
                                <col className="w-[34%]" />
                                <col className="w-[28%]" />
                                <col className="w-[20%]" />
                                <col className="w-[18%]" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className="px-6 py-4 font-bold text-left text-sm text-black bg-[#CDD6DF] rounded-l-md">Company Name</th>
                                    <th className="px-6 py-4 font-bold text-center text-sm text-black bg-[#CDD6DF]">Company Website</th>
                                    <th className="px-6 py-4 font-bold text-center text-sm text-black bg-[#CDD6DF]">Industry</th>
                                    <th className="px-6 py-4 font-bold text-center text-sm text-black bg-[#CDD6DF] rounded-r-md">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {companiesStatus === "loading" ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                                            Loading companies...
                                        </td>
                                    </tr>
                                ) : companiesError ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-8 text-center text-sm text-red-500">
                                            {companiesError}
                                        </td>
                                    </tr>
                                ) : displayedCompanies.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                                            No companies found.
                                        </td>
                                    </tr>
                                ) : (
                                    displayedCompanies.map((company) => (
                                        <tr
                                            key={company.id}
                                            className="transition-colors hover:bg-gray-100"
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex min-w-0 items-center gap-3">
                                                    <ProfileImage
                                                        profilePicture={company.profilePicture}
                                                        accessToken={accessToken || undefined}
                                                        alt={company.name}
                                                        className="min-h-10 min-w-10 max-h-10 max-w-10 rounded-md bg-gray-200 border-2 border-black"
                                                        fallbackPath="/assets/images/Fallback-Company-No-Image.jpg"
                                                    />
                                                    <span
                                                        className="block flex-1 truncate text-sm font-normal text-[#192A47] cursor-pointer"
                                                        onClick={() => navigate(`/company-profile/${company.id}`)}
                                                        title={company.name}
                                                    >
                                                        {company.name}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center text-sm font-normal text-[#192A47]">
                                                {company.website !== "N/A" ? (
                                                    <a
                                                        href={company.website || undefined}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="block truncate text-blue-600 hover:underline"
                                                        onClick={(e) => e.stopPropagation()}
                                                        title={company.website || undefined}
                                                    >
                                                        {company.website}
                                                    </a>
                                                ) : (
                                                    "N/A"
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-center text-sm font-normal text-[#192A47]">
                                                <span className="block truncate" title={company.industry}>
                                                    {company.industry}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-center gap-3">
                                                    <button
                                                        onClick={(e) => handleEdit(company.id, e)}
                                                        className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                                                    >
                                                        <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path fill-rule="evenodd" clip-rule="evenodd" d="M10.8787 0.951903C12.0503 -0.317301 13.9497 -0.317301 15.1213 0.951903L17.2929 3.30444C18.4645 4.57365 18.4645 6.63143 17.2929 7.90063L8.41422 17.5192H18C18.5523 17.5192 19 18.0042 19 18.6025C19 19.2008 18.5523 19.6859 18 19.6859H1C0.447715 19.6859 0 19.2008 0 18.6025V13.1859C0 12.8986 0.105357 12.623 0.292893 12.4198L10.8787 0.951903ZM5.58579 17.5192L15.8787 6.36857C16.2692 5.9455 16.2692 5.25957 15.8787 4.83651L13.7071 2.48397C13.3166 2.0609 12.6834 2.0609 12.2929 2.48397L2 13.6346V17.5192H5.58579Z" fill="black" />
                                                        </svg>

                                                    </button>
                                                    <button
                                                        onClick={(e) => openDeleteModal(company.id, e)}
                                                        disabled={deletingId === company.id}
                                                        className="text-red-600 hover:text-red-800 font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <svg width="19" height="20" viewBox="0 0 24 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M21.1668 5.31824L20.4094 17.6861C20.2159 20.8461 20.1191 22.426 19.3344 23.5619C18.9465 24.1235 18.447 24.5975 17.8677 24.9537C16.6961 25.6742 15.1278 25.6742 11.9913 25.6742C8.85064 25.6742 7.28031 25.6742 6.10789 24.9524C5.52825 24.5955 5.0286 24.1207 4.64078 23.5582C3.85634 22.4204 3.76172 20.8382 3.57246 17.6739L2.8335 5.31824" stroke="#141B34" stroke-width="2" stroke-linecap="round" />
                                                            <path d="M1 5.31793H23M16.957 5.31793L16.1226 3.58055C15.5684 2.42647 15.2912 1.84942 14.8132 1.48954C14.7072 1.40971 14.5949 1.3387 14.4775 1.27721C13.9481 1 13.3128 1 12.0422 1C10.7397 1 10.0884 1 9.55028 1.28883C9.43101 1.35285 9.3172 1.42673 9.21003 1.50972C8.72645 1.88419 8.45632 2.48235 7.91607 3.67867L7.17579 5.31793" stroke="#141B34" stroke-width="2" stroke-linecap="round" />
                                                            <path d="M8.94434 18.8887L8.94434 11.4865" stroke="#141B34" stroke-width="2" stroke-linecap="round" />
                                                            <path d="M15.0557 18.8889L15.0557 11.4867" stroke="#141B34" stroke-width="2" stroke-linecap="round" />
                                                        </svg>

                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination - Client-side pagination with sliding window */}
                {totalPages > 0 && (
                    <div className="mt-6 flex items-center justify-end gap-2">
                        <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1 || companiesStatus === "loading"}
                            className="px-4 py-2 text-sm font-medium text-[#9E9E9E] transition-colors hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            Previous
                        </button>

                        {/* Show page numbers with sliding window */}
                        {visiblePages.map((page) => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                disabled={companiesStatus === "loading"}
                                className={`flex h-9 w-9 items-center justify-center rounded-md text-sm font-bold transition-colors ${currentPage === page ? "bg-black text-white" : "bg-white text-gray-600 hover:bg-gray-100"
                                    } disabled:opacity-50`}
                            >
                                {page}
                            </button>
                        ))}

                        <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages || companiesStatus === "loading"}
                            className="px-4 py-2 text-sm font-medium text-[#9E9E9E] transition-colors hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {/* Delete confirmation modal */}
            {deleteModalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={closeDeleteModal}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="delete-modal-title"
                >
                    <div
                        className="w-full max-w-md rounded-md bg-white p-6 shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 id="delete-modal-title" className="text-lg font-semibold text-gray-900">
                            Delete company
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                            Are you sure you want to delete this company? This action cannot be undone.
                        </p>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={closeDeleteModal}
                                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmDelete}
                                className="btn-cta rounded-md px-4 py-2 text-sm font-medium"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default CompanyList

